<template>
  <RevDialog :name="MODAL_NAME" :title="i18n(translations.modalTitle)">
    <template #body="{ close }">
      <div class="my-24">
        <FormattedMessage :definition="translations.modalDescription">
          <template #link>
            <RevLink :to="QUALITY_CHARTER_LINK" underlined>
              {{ i18n(translations.qualityCharterLinkName) }}
            </RevLink>
          </template>
        </FormattedMessage>
      </div>

      <RevButton
        class="mb-8"
        full-width="always"
        :to="{ type: 'internal', name: ROUTE_NAMES.BADOOM.DOCUSIGN_CGU }"
        variant="primary"
      >
        {{ i18n(translations.goToDocuSignButton) }}
      </RevButton>

      <RevButton
        class="mb-24"
        full-width="always"
        variant="secondary"
        @click="close"
      >
        {{ i18n(translations.cancelButton) }}
      </RevButton>
    </template>
  </RevDialog>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { useRoute } from 'vue-router'

import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { getBrowserCookie } from '@backmarket/utils/cookies/getBrowserCookie'
import { setBrowserCookie } from '@backmarket/utils/cookies/setBrowserCookie'
import { RevButton } from '@ds/components/Button'
import { RevDialog } from '@ds/components/Dialog'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'

import { getExternalLinkList } from '~/constants/externalLinks'
import { ROUTE_NAMES } from '~/constants/routes'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import {
  COOKIE_TERMS_AND_CONDITIONS_NAME,
  MINUTES_COUNTER_WITHOUT_MODAL_IN_MS,
  MODAL_NAME,
  SIGNATURE_PARAM_VALUE,
} from './SignTermsAndConditionsModal.constants'
import translations from './SignTermsAndConditionsModal.translations'

const i18n = useI18n()
const { QUALITY_CHARTER_LINK } = getExternalLinkList(i18n.currentLocale)
const { needToSignNewCgu } = useSellerInfoStore()

const { openSuccessToast } = useTheToast()
const route = useRoute()

const signatureQueryParam = route.query.signature

function setTermsAndConditionsCookie() {
  const currentDate = new Date()
  // Setting up the expiration date, to add 10 min to the current time
  const expirationDate = new Date(
    currentDate.getTime() + MINUTES_COUNTER_WITHOUT_MODAL_IN_MS,
  )

  setBrowserCookie(COOKIE_TERMS_AND_CONDITIONS_NAME, expirationDate.toString())
}

function openSignTermsAndConditionsModal() {
  openModal(MODAL_NAME)
  setTermsAndConditionsCookie()
}

onMounted(() => {
  const cookieDate = new Date(
    getBrowserCookie(COOKIE_TERMS_AND_CONDITIONS_NAME) || '',
  )
  const isCookieDateValid = !Number.isNaN(cookieDate.getTime())
  const isCookieExpried = isCookieDateValid ? cookieDate < new Date() : true

  const shouldDisplayModal =
    (isCookieExpried ||
      signatureQueryParam === SIGNATURE_PARAM_VALUE.DID_NOT_SIGN) &&
    needToSignNewCgu

  if (shouldDisplayModal) {
    openSignTermsAndConditionsModal()
  } else if (
    signatureQueryParam === SIGNATURE_PARAM_VALUE.DID_SIGN &&
    !needToSignNewCgu
  ) {
    openSuccessToast({
      content: i18n(translations.successToastContent),
      title: i18n(translations.successToastTitle),
    })
  }
})
</script>
